import { Link, useLocation, useNavigate } from "react-router-dom";
import { IconHeartFilled, IconArrowLeft, IconSend } from "@tabler/icons-react";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import { formatQuizDate } from "@/lib/date";
import { useState, useEffect, useCallback, useRef } from "react";
import { useUserRealtime } from "@/hooks/useUserRealtime";

const DEEP_PAGES: Record<string, { backTo: string; title: string }> = {
	"/settings/name": { backTo: "/settings", title: "Ubah Nama" },
	"/settings/password": { backTo: "/settings", title: "Ubah Kata Sandi" },
	"/settings/telegram": { backTo: "/settings", title: "Telegram" },
	"/notes/calendar": { backTo: "/notes", title: "Kalender Catatan" },
	"/chat/settings": { backTo: "/chat", title: "Pengaturan Chat" },
	"/reminders/new": { backTo: "/reminders", title: "Buat Pengingat" },
	"/journals/new": { backTo: "/journals", title: "Tulis Jurnal" },
	"/journals/:id/edit": { backTo: "/journals", title: "Edit Jurnal" },
	"/invite": { backTo: "/", title: "Ajak Pasangan" },
	"/share/generate": { backTo: "/referral", title: "Generate Link" },
	"/premium": { backTo: "/settings", title: "Premium" },
	"/referral": { backTo: "/settings", title: "Referral" },
	"/referral/referrals": { backTo: "/referral", title: "Daftar Referral" },
	"/referral/ledger": { backTo: "/referral", title: "Riwayat Komisi" },
	"/referral/bank-accounts": { backTo: "/referral", title: "Rekening Bank" },
	"/referral/withdraw": { backTo: "/referral", title: "Tarik Dana" },
	"/referral/visitors": { backTo: "/referral", title: "Detail Pengunjung" },
};

const HISTORY_DETAIL_RE = /^\/quiz\/history\/(\d{4}-\d{2}-\d{2})$/;
const JOURNAL_DETAIL_RE = /^\/journals\/([^/]+)$/;
const JOURNAL_EDIT_RE = /^\/journals\/([^/]+)\/edit$/;
const REMINDER_EDIT_RE = /^\/reminders\/([^/]+)\/edit$/;
const REMINDER_DETAIL_DATE_RE = /^\/reminders\/date\/(\d{4}-\d{2}-\d{2})$/;
const NOTE_DAY_RE = /^\/notes\/calendar\/(\d{4}-\d{2}-\d{2})$/;

function matchHistoryDetail(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(HISTORY_DETAIL_RE);
	if (!m) return null;
	return { backTo: "/quiz/history", title: formatQuizDate(m[1], { withYear: true }) };
}

function matchJournalDetail(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(JOURNAL_DETAIL_RE);
	if (!m || pathname.match(JOURNAL_EDIT_RE)) return null;
	return { backTo: "/journals", title: "Detail Jurnal" };
}

function matchJournalEdit(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(JOURNAL_EDIT_RE);
	if (!m) return null;
	return { backTo: `/journals/${m[1]}`, title: "Edit Jurnal" };
}

function matchReminderDetailDate(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(REMINDER_DETAIL_DATE_RE);
	if (!m) return null;
	return { backTo: "/reminders", title: "Detail Pengingat" };
}

function matchReminderEdit(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(REMINDER_EDIT_RE);
	if (!m) return null;
	return { backTo: "/reminders", title: "Edit Pengingat" };
}

function matchNoteDay(pathname: string): { backTo: string; title: string } | null {
	const m = pathname.match(NOTE_DAY_RE);
	if (!m) return null;
	return { backTo: "/notes/calendar", title: formatQuizDate(m[1], { withYear: true }) };
}

export function Header() {
	const { isLoaded, isSignedIn, getToken } = useAuth();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const [chatUnread, setChatUnread] = useState(0);
	const activeRef = useRef(true);

	const premiumStatus = useApi(
		(token) => api.premiumStatus(token),
		[isSignedIn],
	);
	const isPremium = premiumStatus.data?.isPremium === true;

	const meApi = useApi((t) => api.me(t), []);
	const hasCouple = Boolean(meApi.data?.couple?.paired);

	useEffect(() => {
		return () => { activeRef.current = false; };
	}, []);

	const fetchUnread = useCallback(async () => {
		const token = await getToken();
		if (!token || !activeRef.current) return;
		try {
			const data = await api.chatUnread(token);
			if (activeRef.current) setChatUnread(data.unreadCount);
		} catch { /* ignore */ }
	}, [getToken]);

	useUserRealtime({
		onChatMessage: () => {
			void fetchUnread();
		},
	});

	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (!isLoaded || !isSignedIn) return;
		void fetchUnread();
		const interval = setInterval(() => { void fetchUnread(); }, 30000);
		return () => clearInterval(interval);
	}, [isLoaded, isSignedIn, fetchUnread]);
	/* eslint-enable react-hooks/set-state-in-effect */

	const deepPage =
		DEEP_PAGES[pathname] ??
		matchHistoryDetail(pathname) ??
		matchJournalDetail(pathname) ??
		matchJournalEdit(pathname) ??
		matchReminderDetailDate(pathname) ??
		matchReminderEdit(pathname) ??
		matchNoteDay(pathname);

	return (
		<header className="sticky top-0 z-30 bg-[color:var(--color-surface)]/90 backdrop-blur border-b border-[color:var(--color-border)]">
			<div className="mx-auto flex max-w-[480px] items-center justify-between px-4 py-3">
				{deepPage ? (
					<button
						type="button"
						onClick={() => navigate(deepPage.backTo)}
						className="flex items-center gap-1 !no-underline text-lg tracking-tight"
						style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--color-text)" }}
					>
						<IconArrowLeft size={20} />
						<span
							style={{
								fontSize: "0.95rem",
								fontWeight: 700,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "200px",
							}}
						>
							{deepPage.title}
						</span>
					</button>
				) : (
					<Link
						to="/"
						className="flex items-center gap-2 font-extrabold text-[color:var(--color-primary)] !no-underline text-lg tracking-tight"
					>
						<IconHeartFilled size={20} /> Yayy!
						{isPremium && (
							<span style={{
								fontSize: "0.55rem", fontWeight: 700,
								padding: "0.1rem 0.375rem", borderRadius: "var(--radius-full)",
								background: "linear-gradient(135deg, #60a5fa, #2563eb)",
								color: "#fff", lineHeight: 1.4, letterSpacing: "0.03em",
							}}>
								💎 Premium
							</span>
						)}
					</Link>
				)}
		<div className="flex items-center gap-2">
			<ThemeToggle />
			{isLoaded && isSignedIn && <NotificationBell />}
			{isLoaded && isSignedIn && hasCouple && (
				<Link to="/chat" aria-label={chatUnread > 0 ? `Chat, ${chatUnread} pesan belum dibaca` : "Chat"} className="relative flex items-center justify-center w-8 h-8 rounded-full" style={{ color: "var(--color-text)" }}>
					<IconSend size={18} />
					{chatUnread > 0 && (
						<span className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full text-white text-[0.65rem] font-bold leading-none" style={{ width: 16, height: 16, background: "var(--color-primary)" }}>
							{chatUnread > 99 ? "99+" : chatUnread}
						</span>
					)}
				</Link>
			)}
					{isLoaded && !isSignedIn && (
						<>
							<Link
								to="/login"
								className="btn btn-ghost"
								style={{ padding: "0.35rem 0.7rem", fontSize: "0.82rem" }}
							>
								Masuk
							</Link>
							<Link
								to="/register"
								className="btn btn-primary"
								style={{ padding: "0.35rem 0.7rem", fontSize: "0.82rem" }}
							>
								Daftar
							</Link>
						</>
					)}
				</div>
			</div>
		</header>
	);
}
